import { useCallback, useMemo, useState } from "react";

/**
 * Small selection state for bulk admin actions.
 * Usage:
 *   const sel = useBulkSelection();
 *   sel.toggle(id)             — toggle one row
 *   sel.toggleAll(ids)         — toggle whole page (based on current state)
 *   sel.isSelected(id) -> bool
 *   sel.ids  -> array of selected ids
 *   sel.count / sel.clear()
 */
export default function useBulkSelection() {
  const [set, setSet] = useState(() => new Set());

  const toggle = useCallback((id) => {
    setSet((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }, []);

  const toggleAll = useCallback((ids, forceOn) => {
    setSet((s) => {
      const allOn = ids.length > 0 && ids.every((id) => s.has(id));
      const on = forceOn ?? !allOn;
      const n = new Set(s);
      if (on) ids.forEach((id) => n.add(id)); else ids.forEach((id) => n.delete(id));
      return n;
    });
  }, []);

  const clear = useCallback(() => setSet(new Set()), []);
  const isSelected = useCallback((id) => set.has(id), [set]);
  const ids = useMemo(() => [...set], [set]);

  return { toggle, toggleAll, clear, isSelected, ids, count: ids.length };
}
